/**
 * AIQuickAddFab — Phase 13 AI Transaction Entry Assistant.
 *
 * Floating action button (bottom-right) that opens a Sheet on the right side
 * with a prompt textarea, an "Analyze" button, the AI suggestion preview,
 * and a Confirm flow. Visible only to roles that can create transactions.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sparkles, Loader2, AlertTriangle, CheckCircle2, Pencil, Mic, MicOff, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { fmtUSD } from "@/lib/tx";

const WRITE_ROLES = new Set([
  ROLES.SYSTEM_ADMIN, ROLES.SALIHI_ACCOUNTANT,
  ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER,
  ROLES.SOMTURK_DATA_ENTRY,
]);

function ConfidenceChip({ value }) {
  const v = Number(value || 0);
  const label = v >= 0.75 ? "High" : v >= 0.5 ? "Medium" : "Low";
  const cls = v >= 0.75
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : v >= 0.5
    ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-red-100 text-red-700 border-red-200";
  return (
    <Badge className={cls} data-testid="ai-confidence-chip">
      Confidence {label} · {Math.round(v * 100)}%
    </Badge>
  );
}

export default function AIQuickAddFab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const visible = WRITE_ROLES.has(user?.role);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // { suggestion, warnings, duplicates, suggestion_id, raw_response, model_used }
  const [edited, setEdited] = useState(null);
  const [createNewParty, setCreateNewParty] = useState(false);
  const [dupAck, setDupAck] = useState(false);
  const [listening, setListening] = useState(false);

  const speechSupported = useMemo(() => {
    if (typeof window === "undefined") return false;
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, []);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const txt = e.results[0]?.[0]?.transcript || "";
      setPrompt((p) => (p ? `${p} ${txt}` : txt));
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    setListening(true);
    rec.start();
  };

  const reset = () => {
    setResult(null);
    setEdited(null);
    setCreateNewParty(false);
    setDupAck(false);
  };

  const analyze = async () => {
    if (prompt.trim().length < 3) {
      toast.error("Type a few words first.");
      return;
    }
    setAnalyzing(true);
    reset();
    try {
      const { data } = await api.post("/ai/parse-transaction", { prompt: prompt.trim() });
      setResult(data);
      setEdited({ ...data.suggestion, suggestion_id: data.suggestion_id });
      setCreateNewParty(false);
      setDupAck(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const updateField = (k, v) => setEdited((s) => ({ ...s, [k]: v }));

  const dupBlocked = result?.duplicates?.some((d) => d.similarity >= 0.85) && !dupAck;
  const confLow = (edited?.confidence ?? 0) < 0.5;
  const canConfirm = !analyzing && !confirming && edited
    && Number(edited?.amount || 0) > 0
    && edited?.transaction_type
    && edited?.description
    && !dupBlocked
    && !confLow;

  const confirm = async () => {
    if (!edited) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/ai/confirm-transaction", {
        suggestion: edited,
        create_new_parties: createNewParty,
        duplicate_acknowledged: dupAck,
        original_prompt: prompt,
        raw_response: result?.raw_response,
        model_used: result?.model_used,
      });
      toast.success("Transaction created in draft status.");
      setOpen(false);
      // Reset and navigate
      const id = data.transaction_id;
      setPrompt("");
      reset();
      navigate(`/transactions/${id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  const editAndRefine = () => {
    if (!edited) return;
    // Stash for the New-Transaction form to consume
    sessionStorage.setItem("ai_prefill_tx", JSON.stringify({
      ...edited, ai_source_prompt: prompt,
    }));
    setOpen(false);
    navigate("/transactions/new?ai=1");
  };

  if (!visible) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full
                     bg-gradient-to-r from-[#4FA3D1] to-[#2F86B5] text-white shadow-xl
                     px-5 py-3 text-sm font-semibold hover:shadow-2xl hover:-translate-y-0.5
                     transition-all"
          data-testid="ai-quick-add-fab"
          aria-label="Quick Add Transaction with AI"
        >
          <Sparkles className="h-4 w-4" />
          Quick Add (AI)
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="ai-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#4FA3D1]" />
            AI Transaction Assistant
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <Label>Describe the transaction in plain English</Label>
          <Textarea
            rows={3}
            placeholder="Try: 'Paid $500 cash to Acme Office for stationery yesterday' or 'Received $2,000 from Atlas Trading for invoice INV-2026-001'"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="ai-prompt"
            disabled={analyzing}
          />
          <div className="flex gap-2">
            <Button
              onClick={analyze}
              disabled={analyzing || prompt.trim().length < 3}
              className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white flex-1"
              data-testid="ai-analyze-btn"
            >
              {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Analyze with AI
            </Button>
            {speechSupported && (
              <Button
                type="button"
                variant="outline"
                onClick={startVoice}
                disabled={listening || analyzing}
                title="Voice-to-text"
                data-testid="ai-voice-btn"
              >
                {listening ? <MicOff className="h-4 w-4 animate-pulse" /> : <Mic className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {result && edited && (
            <div className="space-y-4" data-testid="ai-result">
              <div className="flex flex-wrap items-center gap-2">
                <ConfidenceChip value={edited.confidence} />
                {(edited.accounting_impact?.affects_modules || []).map((m) => (
                  <Badge key={m} variant="outline" className="text-[#2F86B5] border-[#4FA3D1]">{m}</Badge>
                ))}
                {edited.ai_source_prompt && (
                  <Badge variant="outline" className="bg-violet-50 border-violet-200 text-violet-700">
                    AI-suggested
                  </Badge>
                )}
              </div>

              {result.warnings?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardContent className="p-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <ul className="text-sm space-y-1 text-amber-900">
                      {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Type" v={edited.transaction_type} on={(v) => updateField("transaction_type", v)} testid="ai-field-type" />
                <Field label="Date" v={edited.transaction_date} on={(v) => updateField("transaction_date", v)} type="date" testid="ai-field-date" />
                <Field label="Amount (USD)" v={edited.amount} on={(v) => updateField("amount", Number(v))} type="number" testid="ai-field-amount" />
                <Field label="Payment method" v={edited.payment_method} on={(v) => updateField("payment_method", v)} />
                <Field label="Counterparty" v={edited.counterparty_name} on={(v) => updateField("counterparty_name", v)} testid="ai-field-counterparty" />
                <Field label="Counterparty type" v={edited.counterparty_type} on={(v) => updateField("counterparty_type", v)} />
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    rows={2} value={edited.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    data-testid="ai-field-description"
                  />
                </div>
              </div>

              <Card>
                <CardContent className="p-3 text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Accounting impact</div>
                  <div className="grid grid-cols-2 gap-3" data-testid="ai-accounting-impact">
                    <div>
                      <div className="text-[11px] text-emerald-700 font-semibold mb-1">Debits</div>
                      {(edited.accounting_impact?.debits || []).map((d, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="font-mono">{d.account_code}</span>
                          <span className="text-slate-600 truncate ml-2">{d.account_name}</span>
                          <span className="tabular-nums ml-2">{fmtUSD(d.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-[11px] text-red-700 font-semibold mb-1">Credits</div>
                      {(edited.accounting_impact?.credits || []).map((c, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="font-mono">{c.account_code}</span>
                          <span className="text-slate-600 truncate ml-2">{c.account_name}</span>
                          <span className="tabular-nums ml-2">{fmtUSD(c.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {edited.new_party_suggested && edited.counterparty_name && (
                <label className="flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50/60 p-3 cursor-pointer" data-testid="ai-new-party-toggle">
                  <input
                    type="checkbox" checked={createNewParty}
                    onChange={(e) => setCreateNewParty(e.target.checked)}
                    className="mt-1"
                    data-testid="ai-new-party-checkbox"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-violet-900">
                      Create new {edited.counterparty_type || "party"}: "{edited.counterparty_name}"
                    </div>
                    <div className="text-xs text-violet-700/80">
                      The master record will be added to /{(edited.counterparty_type || "other") + "s"} when you confirm.
                    </div>
                  </div>
                </label>
              )}

              {result.duplicates?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/40">
                  <CardContent className="p-3 text-sm">
                    <div className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Possible duplicate transactions
                    </div>
                    <ul className="space-y-1 text-xs text-amber-900" data-testid="ai-duplicate-list">
                      {result.duplicates.map((d) => (
                        <li key={d.tx_id} className="flex justify-between gap-2">
                          <span>{d.date} · {fmtUSD(d.amount)} · {d.counterparty || "—"} · {d.description || ""}</span>
                          <a className="text-[#2F86B5] hover:underline" href={`/transactions/${d.tx_id}`} target="_blank" rel="noreferrer">View</a>
                        </li>
                      ))}
                    </ul>
                    {result.duplicates.some((d) => d.similarity >= 0.85) && (
                      <label className="flex items-center gap-2 mt-2 text-xs">
                        <input
                          type="checkbox" checked={dupAck}
                          onChange={(e) => setDupAck(e.target.checked)}
                          data-testid="ai-dup-ack"
                        />
                        I confirm this is not a duplicate of one of the above.
                      </label>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={editAndRefine} className="flex-1" data-testid="ai-edit-btn">
                  <Pencil className="h-4 w-4 mr-1" /> Edit &amp; refine
                </Button>
                <Button
                  onClick={confirm}
                  disabled={!canConfirm}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                  data-testid="ai-confirm-btn"
                  title={confLow ? "Confidence too low — please refine" : (dupBlocked ? "Acknowledge possible duplicate first" : "Confirm")}
                >
                  {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Confirm &amp; create <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid} />
    </div>
  );
}
